/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect } from "react";

import { useCurrentStateAndParams, useRouter } from "@uirouter/react";
import styled, { css } from "styled-components";

import { colorsPalettes, mixins, fonts } from "@similarweb/styles";
import type { IColumnsPickerLiteProps } from "@similarweb/ui-components";
import { SearchInput, ColumnsPickerLite } from "@similarweb/ui-components";

import { chosenSites } from "app/common/services/chosenSites";
import { swSettings } from "app/common/services/swSettings";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { getExcelButtonText } from "app/components/React/ExcelButton/getExcelButtonText";
import { i18nFilter } from "app/filters/i18nFilter";
import { changeFilter, minVisitsAbbrFilter, percentageFilter } from "app/filters/ngFilters";
import { numberFilter } from "app/filters/numberFilter";
import { SocialSourcesFilter } from "app/pages/digital-marketing/social/filters/SocialSourcesFilter";
import { CommonTableOverview } from "app/pages/website-analysis/traffic-sources/display-ads/publishers/common/PublishersOverview";
import {
    CompetitiveCompareBarsStyled,
    CompetitiveCompareBarsTitle,
    DistributionBox,
    DownloadExcelContainer,
    Right,
    SearchContainer,
} from "app/pages/website-analysis/traffic-sources/display-ads/publishers/common/StyledComponents";
import { DistributionByNetworks } from "app/pages/website-analysis/traffic-sources/display-ads/publishers/compare/DistributionByNetworks";
import { isPaidExcelAllowed } from "app/services/PaidExcelService/isPaidExcelAllowed";
import { allTrackers } from "app/services/track/track";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

const Section = styled.div<{ margin?: number; height?: number }>`
    padding: 0 16px;
    display: flex;
    align-items: center;
    height: ${({ height }) => height}px;
    ${({ margin }) =>
        margin &&
        css`
            margin: ${margin}px 0;
        `};
    width: 100%;
`;

const Separator = styled.hr<{ margin?: number }>`
    border-top-color: ${colorsPalettes.carbon[50]};
    margin: 0;
`;

const StyledDistributionBox = styled(DistributionBox)`
    width: auto;
`;

const Title = styled.div`
    ${mixins.setFont({
        $size: 20,
        $color: colorsPalettes.carbon[500],
        $weight: 500,
        $family: fonts.$dmSansFontFamily,
    })};
    margin-bottom: 28px;
    margin-left: 24px;
    margin-top: 24px;
`;

export const SocialOverviewPageTableTop = ({
    tableData,
    sources,
    onFilterChange,
    filtersStateObject,
    overviewData,
    tableColumns,
    onClickToggleColumns,
    excelLink,
    isCompare,
    totalCount,
}) => {
    const i18n = i18nFilter();
    const { params, state } = useCurrentStateAndParams();
    const router = useRouter();
    const isPaidExcelPermitted = isPaidExcelAllowed();
    useEffect(() => {
        if (params.source !== filtersStateObject.source) {
            if (params.source) {
                onFilterChange({ source: params.source });
            } else {
                onFilterChange({ source: null });
            }
        }
    }, [params.source, filtersStateObject]);
    const onColumnToggle = (key) => {
        if (tableColumns[key].visible) {
            allTrackers.dispatchTrackEvent("display.ads.publishers.table.excel.remove.column", "remove", "", {
                column_name: tableColumns[key].displayName,
            });
        } else {
            allTrackers.dispatchTrackEvent("display.ads.publishers.table.excel.add.column", "add", "", {
                column_name: tableColumns[key].displayName,
            });
        }
        onClickToggleColumns(parseInt(key, 10));
    };
    const getColumnsPickerLiteProps = (): Omit<IColumnsPickerLiteProps, "translations"> => {
        const columns = tableColumns.reduce((res, col, index) => {
            if (!col.fixed) {
                return [
                    ...res,
                    {
                        key: index.toString(),
                        displayName: col.displayName,
                        visible: col.visible,
                    },
                ];
            }
            return res;
        }, []);
        return {
            columns,
            onColumnToggle,
            onPickerToggle: () => null,
            maxHeight: 264,
            width: "auto",
        };
    };
    const downloadExcelPermitted = swSettings.current.resources.IsExcelAllowed;
    const excelDownloadUrl = downloadExcelPermitted ? excelLink : "";
    const trackExcelDownload = () => {
        allTrackers.dispatchTrackEvent("Download", "not permitted", "Table/Excel");
    };
    const items = [
        {
            value: minVisitsAbbrFilter()(overviewData.total * tableData.TotalShare),
            text: i18n("analysis.common.trafficsource.social.ref"),
        },
        {
            value: percentageFilter()(tableData.TotalShare > 0.99 ? 1 : tableData.TotalShare || 0, 2) + "%",
            text: `${i18n("shared.totals.total")} ${i18n("analysis.common.trafficsource.social.visits")}`,
        },
        {
            value: numberFilter()(tableData.TotalUnGroupedCount || tableData.TotalCount),
            text: i18n("analysis.common.trafficsource.social.title"),
        },
    ];

    const selectedSource = filtersStateObject.source;
    const onChange = (source) => {
        const sourceParameter = { source: source ? source.children : null };
        // TODO: consider use new navigator instead of directly access the router
        router.stateService.go(state, sourceParameter);
        onFilterChange(sourceParameter);
    };
    const onSearch = (term) => {
        const sourceParameter = { search: term ? term : null };
        // TODO: consider use new navigator instead of directly access the router
        router.stateService.go(state, sourceParameter);
        onFilterChange(sourceParameter);
    };
    const getDomains = () => {
        return filtersStateObject.key.split(",").map((site: string) => {
            return {
                color: chosenSites.getSiteColor(site),
                name: site,
            };
        });
    };
    const getLink = (key, value) => {
        // TODO: consider use new navigator instead of directly access the router
        return router.stateService.href(state.name, {
            ...params,
            source: value,
        });
    };

    const transformShareBarData = (data) => {
        const res = [];
        Object.entries(data).forEach(([domain, percentage]) => {
            if (percentage !== "NaN") {
                res.push({
                    color: chosenSites.getSiteColor(domain),
                    name: domain,
                    percentage,
                    valueText: changeFilter()(percentage),
                    width: (percentage as number) * 100,
                });
            }
        });
        return res;
    };
    const competitiveTrafficShare = transformShareBarData(tableData.TotalVisitsGlobalList);
    return (
        <>
            <Section height={64}>
                <SocialSourcesFilter
                    sources={sources}
                    selectedSource={selectedSource}
                    onChange={onChange}
                />
            </Section>
            <Separator />
            <Section margin={19}>
                <CommonTableOverview items={items} />
            </Section>
            {isCompare ? (
                <div>
                    <Separator />
                    <CompetitiveCompareBarsTitle>
                        {i18n("display.ads.publishers.compare.traffic.bar.title")}
                    </CompetitiveCompareBarsTitle>
                    <CompetitiveCompareBarsStyled
                        items={[{ bars: competitiveTrafficShare }]}
                        isFetching={false}
                        title={i18n("analysis.compare.trafficsource.referrals.tooltip.title")}
                    />
                    <StyledDistributionBox>
                        <Title>{i18n("analysis.common.trafficsource.social.sources")}</Title>
                        <DistributionByNetworks
                            networksDistribution={tableData.Sources}
                            domains={getDomains()}
                            getLink={getLink}
                            topNetworks={tableData.TopSources.map(({ Name }) => Name)}
                            thresholdForOthers={0}
                            emptyStateKey="global.nodata.notavilable"
                        />
                    </StyledDistributionBox>
                </div>
            ) : null}
            <SearchContainer>
                <SearchInput
                    disableClear={true}
                    defaultValue={filtersStateObject.search}
                    debounce={500}
                    onChange={onSearch}
                    placeholder={i18n("forms.search.placeholder")}
                />
                <Right>
                    <FlexRow>
                        <DownloadExcelContainer>
                            <DownloadButtonMenu
                                PaidExcel={isPaidExcelPermitted}
                                totalCount={totalCount}
                                Excel={true}
                                iconNameProps={!downloadExcelPermitted ? "excel-download" : null}
                                downloadUrl={excelDownloadUrl}
                                exportFunction={trackExcelDownload}
                                excelLocked={!downloadExcelPermitted}
                                buttonText={getExcelButtonText()}
                            />
                        </DownloadExcelContainer>
                        <div>
                            <ColumnsPickerLite
                                {...getColumnsPickerLiteProps()}
                                withTooltip
                                translations={{
                                    tooltip: i18n("workspace.sales.column.picker"),
                                    getSelectedAmountText: ({ selectedValue, totalValue }) =>
                                        i18n("common.columnsPicker.selectedAmount", {
                                            selected: selectedValue,
                                            total: totalValue,
                                        }),
                                    mandatoryField: i18n("common.columnsPicker.mandatoryField"),
                                    apply: i18n("common.apply"),
                                }}
                            />
                        </div>
                    </FlexRow>
                </Right>
            </SearchContainer>
        </>
    );
};
